import { CreditLineBlockReason, CreditLineMark, LinePresentation, PhoneErrorCode } from '../models';

/**
 * Créditos v2 (FINA-3378): reglas del MONTO que pide el comercio, no del cupo.
 *
 * El cupo, el nivel, los términos y el cronograma los calcula `api` y llegan en
 * `GET /creditos/line`: la pantalla de línea no tiene ninguna tabla de montos por nivel
 * (ni `LEVEL_AMOUNTS`, que es la escalera v1 de ofertas).
 *
 * Espejo de `MIN_REQUEST_USD` y `CUPO_STEP_USD` de `api/src/modules/credit-line/creditLine.constants.ts`
 * (DT-5 y DT-6): el servidor rechaza con 400 `CREDIT_LINE_AMOUNT_INVALID` lo que no cumpla.
 */
export const LINE_MIN_REQUEST_USD = 100;
export const LINE_AMOUNT_STEP_USD = 50;

/**
 * Línea en pausa por inactividad (`suspended`, 18/10): el copy no nombra las ventas ni los días sin
 * vender, para que el comercio no registre una sola venta solo para volver a pedir.
 */
export const LINE_PAUSED_ACTION = 'Registra los movimientos de tu negocio para reactivar la línea.';

/** Largo máximo del texto de la solicitud de recálculo (DT-12), igual que `RECALC_MESSAGE_MAX` de `api`. */
export const RECALC_MESSAGE_MAX = 1000;

/** Día de atraso en que una cuota apaga la línea (castigo). Solo se nombra en el copy. */
export const LINE_WRITE_OFF_DAY = 61;

// ─── Copy de la pantalla de línea (tuteo) ────────────────────────────────────────────────────
// `GET /creditos/line` no trae textos: los motivos se traducen aquí, en mapas exhaustivos por
// tipo. Un valor que el servidor agregue y el front no conozca cae al `unknown` / default.

export interface LineNoticeCopy {
    tone: 'danger' | 'warn' | 'info';
    title: string;
    body: string;
}

/** Texto del estado sin acceso (`not_eligible`) del prototipo. */
const LINE_LOCKED_BODY = 'Paga tu suscripción de Fina a tiempo, registra todas tus ventas y gastos semanalmente durante los primeros 60 días. Así pasas al siguiente nivel y podrás pedir tu préstamo.';

/**
 * Pausa del día por el tope diario de Fina (`blockReason: 'daily_limit'`). Copy del handoff
 * `FINA-3378-limite-diario-pausa` (no está en el canvas): sin cifras del tope ni de lo restante.
 * Única fuente para el aviso (`LINE_BLOCK_NOTICES.daily_limit`) y la franja (`lineStripCopy`).
 */
const DAILY_PAUSE_COPY = { title: 'Mañana tu línea de crédito se va activar nuevamente', sub: 'Mañana podrás pedir tu crédito' };

/**
 * Recálculo abierto (`blockReason: 'recalc_pending'`, FINA-3412): mientras el equipo lo revisa no se
 * puede pedir. Copy decidido en el ticket. Única fuente para el aviso
 * (`LINE_BLOCK_NOTICES.recalc_pending`, también el pie de Niveles) y la franja (`lineStripCopy`).
 */
const RECALC_PENDING_COPY = {
    title: 'Estamos revisando tu solicitud de recálculo',
    body: 'Mientras la revisamos no puedes pedir un préstamo. Te responderemos en esta misma pantalla.',
    stripSub: 'Podrás pedir tu préstamo cuando te respondamos',
};

/** Por qué no se puede pedir. Copy literal del diseño Fina Presta (revisión del 16/10). */
export const LINE_BLOCK_NOTICES: Record<Exclude<CreditLineBlockReason, null> | 'unknown', LineNoticeCopy> = {
    written_off: { tone: 'danger', title: 'Tu línea está apagada', body: `Un préstamo llegó al día ${LINE_WRITE_OFF_DAY} sin pagarse. No puedes pedir nuevos préstamos con esta línea.` },
    // Sin acceso, la card pinta la cabecera `LINE_LOCKED_COPY`; este título queda para el pie de Niveles.
    not_eligible: { tone: 'info', title: 'Desbloquea tu línea de crédito', body: LINE_LOCKED_BODY },
    suspended: {
        tone: 'warn',
        title: 'Tu línea está en pausa',
        body: LINE_PAUSED_ACTION,
    },
    open_credit: { tone: 'info', title: 'Tienes un crédito en curso', body: 'Podrás pedir otro cuando lo termines de pagar.' },
    below_floor: {
        tone: 'warn',
        title: `Tu cupo es menor a $${LINE_MIN_REQUEST_USD}`,
        body: `Necesitas un cupo de al menos $${LINE_MIN_REQUEST_USD} para pedir. Tu cupo crece con tus ventas.`,
    },
    daily_limit: { tone: 'info', title: DAILY_PAUSE_COPY.title, body: `${DAILY_PAUSE_COPY.sub}.` },
    recalc_pending: { tone: 'info', title: RECALC_PENDING_COPY.title, body: RECALC_PENDING_COPY.body },
    unknown: { tone: 'info', title: 'No puedes pedir un crédito ahora', body: 'Tu línea no permite pedir un crédito en este momento.' },
};

/** Redondeo hacia abajo al múltiplo de $50, acotado a [mín, máx]. `null` si el rango está vacío. */
export const clampLineAmount = (value: number, maxUSD: number): number | null => {
    const max = Math.floor((Number(maxUSD) || 0) / LINE_AMOUNT_STEP_USD) * LINE_AMOUNT_STEP_USD;
    if (max < LINE_MIN_REQUEST_USD) return null;
    const v = Number(value);
    if (!Number.isFinite(v)) return max;
    const snapped = Math.floor(v / LINE_AMOUNT_STEP_USD) * LINE_AMOUNT_STEP_USD;
    return Math.min(max, Math.max(LINE_MIN_REQUEST_USD, snapped));
};

/** Paso del slider de mobile: puntos de $100 en $100 desde `LINE_MIN_REQUEST_USD` (monto personalizado, 18/10). */
export const LINE_MOBILE_STEP_USD = 100;

/** Máximo pedible: el cupo en múltiplos de $50. `null` si no llega al mínimo. */
export const lineMaxAmount = (maxUSD: number): number | null => {
    const max = Math.floor((Number(maxUSD) || 0) / LINE_AMOUNT_STEP_USD) * LINE_AMOUNT_STEP_USD;
    return max >= LINE_MIN_REQUEST_USD ? max : null;
};

/** Lo mismo que valida `api`: múltiplo de $50 entre `LINE_MIN_REQUEST_USD` y el cupo. */
export const isValidLineAmount = (value: unknown, maxUSD: number): boolean => {
    const v = Number(value);
    const max = lineMaxAmount(maxUSD);
    return max != null && Number.isInteger(v) && v % LINE_AMOUNT_STEP_USD === 0 && v >= LINE_MIN_REQUEST_USD && v <= max;
};

/**
 * Monto del recálculo (DT-42): el menor que acepta `api`, el múltiplo de $50 de arriba del cupo y al menos
 * `LINE_MIN_REQUEST_USD` (cupo $750 → $800; $730 → $750). Sin cupo, $100.
 */
export const minRecalcAmount = (cupoUSD: number | null | undefined): number => {
    const cupo = Math.max(0, Number(cupoUSD) || 0);
    return Math.max(LINE_MIN_REQUEST_USD, (Math.floor(cupo / LINE_AMOUNT_STEP_USD) + 1) * LINE_AMOUNT_STEP_USD);
};

/**
 * Lo mismo que valida `createRecalcRequest` de `api` (`amountShapeError` + «mayor que el cupo»): múltiplo
 * de $50, al menos `LINE_MIN_REQUEST_USD` y **mayor** que el cupo actual, sin tope. No uses
 * `isValidLineAmount`, `clampLineAmount` ni `roundUpLineAmount`: acotan al cupo.
 */
export const isValidRecalcAmount = (value: unknown, cupoUSD: number | null | undefined): boolean => {
    const v = typeof value === 'number' ? value : NaN;
    const cupo = Math.max(0, Number(cupoUSD) || 0);
    return Number.isInteger(v) && v % LINE_AMOUNT_STEP_USD === 0 && v >= LINE_MIN_REQUEST_USD && v > cupo;
};

/**
 * Monto escrito a mano, al terminar de escribir (18/10): el múltiplo de $50 de arriba, dentro de [$100, cupo]
 * ($120 → $150, $30 → $100, sobre el cupo → el cupo). `null` sin monto o sin cupo pedible.
 */
export const roundUpLineAmount = (value: unknown, maxUSD: number): number | null => {
    const v = Number(value);
    const max = lineMaxAmount(maxUSD);
    if (max == null || !Number.isFinite(v) || v <= 0) return null;
    const up = Math.ceil(v / LINE_AMOUNT_STEP_USD) * LINE_AMOUNT_STEP_USD;
    return Math.min(max, Math.max(LINE_MIN_REQUEST_USD, up));
};

/** Paradas del slider de mobile: de $100 en $100 y, si el cupo no cae en una, el cupo al final. */
export const lineMobileStops = (maxUSD: number): number[] => {
    const max = lineMaxAmount(maxUSD);
    if (max == null) return [];
    const stops: number[] = [];
    for (let v = LINE_MIN_REQUEST_USD; v <= max; v += LINE_MOBILE_STEP_USD) stops.push(v);
    if (stops[stops.length - 1] !== max) stops.push(max);
    return stops;
};

/** Cuántos montos ofrece la calculadora (diseño «Préstamos v2»: grilla de 6 / slider de 6 paradas). */
export const LINE_AMOUNT_OPTIONS_COUNT = 6;
/** Primer monto de la calculadora cuando el cupo lo permite (`lo = mín($250, cupo)` del diseño). */
export const LINE_AMOUNT_OPTIONS_FLOOR_USD = 250;

/**
 * Montos que ofrece la calculadora, como el prototipo: `lo = mín($250, cupo)` y 6 paradas
 * repartidas entre `lo` y el cupo, redondeadas a $50; la última es siempre el cupo. Sin repetidos,
 * en orden. Vacío si el cupo no llega al mínimo pedible.
 */
export const lineAmountOptions = (maxUSD: number): number[] => {
    const cupo = Math.floor((Number(maxUSD) || 0) / LINE_AMOUNT_STEP_USD) * LINE_AMOUNT_STEP_USD;
    if (cupo < LINE_MIN_REQUEST_USD) return [];
    const lo = Math.min(LINE_AMOUNT_OPTIONS_FLOOR_USD, cupo);
    const last = LINE_AMOUNT_OPTIONS_COUNT - 1;
    const opts: number[] = [];
    for (let i = 0; i <= last; i++) {
        const v = i === last ? cupo : Math.round((lo + ((cupo - lo) * i) / last) / LINE_AMOUNT_STEP_USD) * LINE_AMOUNT_STEP_USD;
        if (v >= LINE_MIN_REQUEST_USD && v <= cupo && !opts.includes(v)) opts.push(v);
    }
    return opts;
};

/**
 * Aviso de mora del dashboard v1 de ofertas. Es el mismo texto de la landing de ofertas
 * (`credits-landing`); los días de gracia salen de `LATE_GRACE_CALENDAR_DAYS`
 * (`utils/abono-plan.util.ts`), nunca de un literal. La línea v2 usa `LINE_LATE_FEE_NOTICE`.
 */
export const LATE_FEE_NOTICE = {
    title: 'Fee de reactivación por mora',
    body: (graceDays: number): string =>
        `Cuentas con ${graceDays} días de gracia para pagar cada cuota después de su vencimiento. Al día ${
            graceDays + 1
        } se bloquea el acceso a Fina y para reactivarlo deberás pagar la cuota más un fee de reactivación.`,
};

/**
 * ADM-799 — aviso al comercio cuando soporte rechazó su último pago o abono (`visibleRejection`): tarjeta de
 * la cuota (v1 y línea v2) y modal de bloqueo por mora. El motivo es texto del admin y va tal cual, sin
 * recortar, con interpolación `{{ }}`; sin motivo (`null`, vacío o solo espacios), «Pago rechazado.».
 */
export const PAYMENT_REJECTED_NOTICE = {
    text: (note: string | null | undefined): string => (typeof note === 'string' && note.trim() ? `Pago rechazado: ${note}` : 'Pago rechazado.'),
};

// ─── Fina Presta (FINA-3378, diseño «Préstamos v2 — Niveles de crédito») ─────────────────────
// Los montos llegan ya formateados (`formatLineUsd` de `utils/line-format.util.ts`): este archivo
// no importa utils, para que los utils puedan importar el copy sin ciclo.

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** Une partes en español: «a», «a y b», «a, b y c». */
export const joinEs = (parts: string[]): string => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`);

const ORDINALS_ES = ['1er', '2do', '3er', '4to', '5to', '6to', '7mo', '8vo', '9no', '10mo'];

/** Ordinal abreviado: 1er … 10mo. Fuera de 1–10, `día N` (para «Al día 12»). */
export const ordinalEs = (n: number): string => ORDINALS_ES[n - 1] ?? `día ${n}`;

/** «4to día» / «día 12»: la frase completa, para no duplicar «día» fuera de la tabla. */
const dayOrdinalPhrase = (n: number): string => (ORDINALS_ES[n - 1] ? `${ORDINALS_ES[n - 1]} día` : `día ${n}`);

/**
 * Aviso de mora de la línea v2: calculadora (`credit-line`) y préstamo activo con `lineMode`
 * (`loan-dashboard`). `LATE_FEE_NOTICE` queda igual para el dashboard v1 de ofertas.
 */
export const LINE_LATE_FEE_NOTICE = {
    title: 'Fee de reactivación por mora',
    body: (graceDays: number): string =>
        `Tienes ${graceDays} días de gracia para pagar la cuota próxima a vencer. Al ${dayOrdinalPhrase(
            graceDays + 1,
        )} tu cuenta va a ser bloqueada y, para reactivarla, deberás pagar la cuota más un fee de reactivación.`,
};

function assertNever(value: never): never {
    throw new Error(`Presentación de línea desconocida: ${(value as { kind?: string })?.kind}`);
}

/** Nivel con el que arranca toda línea (el primero de la escalera de api). */
const LINE_BASE_LEVEL = 2;

/** Tono del ícono de la franja. `warn` solo con el depósito demorado (`LINE_DEPOSIT_DELAY_COPY.strip`). */
export type LineStripTone = 'brand' | 'paused' | 'off' | 'muted' | 'warn';

export interface LineStripCopy {
    text: string;
    /** Remate resaltado tras `text` (cian; ámbar con `warn`). El espacio lo pone la plantilla. */
    hi?: string;
    sub: string;
    tone: LineStripTone;
}

/**
 * Copy de la franja «Fina Presta» por `kind`. Única fuente: un `kind` nuevo no compila hasta
 * tener su caso (`assertNever`). `ready`, `in_loan`, `paused` y `off` son los del prototipo.
 */
export function lineStripCopy(p: LinePresentation): LineStripCopy {
    switch (p.kind) {
        // En revisión el diseño sigue mostrando la franja de la línea lista (pantalla «¡Solicitud enviada!»).
        case 'ready':
        case 'in_review':
            return { text: 'Tendrás tu crédito en menos de', hi: '30 minutos', sub: 'Sin papeleo ni filas', tone: 'brand' };
        case 'in_loan':
            return { text: 'Tienes un préstamo activo · págalo a tiempo para subir de nivel', sub: 'Sin papeleo ni filas', tone: 'brand' };
        case 'paused':
            return { text: 'Tu línea está en pausa', sub: LINE_PAUSED_ACTION, tone: 'paused' };
        case 'off':
            return { text: 'Tu línea de Fina Presta está apagada', sub: `Una cuota llegó al día ${LINE_WRITE_OFF_DAY} sin pagarse`, tone: 'off' };
        case 'no_access':
            return { text: 'Tu línea se activa en el', hi: `Nivel ${LINE_BASE_LEVEL}`, sub: 'Sigue registrando en Fina para llegar', tone: 'muted' };
        case 'below_floor':
            return { text: `Tu cupo todavía es menor a $${LINE_MIN_REQUEST_USD} · crece con tus ventas`, sub: 'Sin papeleo ni filas', tone: 'muted' };
        // Distinta de `paused` (sin ventas): no habla de ventas ni de días; reutiliza su tono.
        case 'paused_today':
            return { text: DAILY_PAUSE_COPY.title, sub: DAILY_PAUSE_COPY.sub, tone: 'paused' };
        case 'recalc_pending':
            return { text: RECALC_PENDING_COPY.title, sub: RECALC_PENDING_COPY.stripSub, tone: 'muted' };
        default:
            return assertNever(p);
    }
}

/** Pieza del texto del banner del Home: texto plano, el logo de Fina Presta o un monto (en cian). */
export type BannerSegment = { kind: 'text'; text: string } | { kind: 'logo' } | { kind: 'amount'; usd: number };

export interface BannerCopy {
    segments: BannerSegment[];
    cta: string;
}

const LOGO_SEGMENT: BannerSegment = { kind: 'logo' };
const textSegment = (text: string): BannerSegment => ({ kind: 'text', text });
const amountSegment = (usd: number): BannerSegment => ({ kind: 'amount', usd });

/**
 * Copy del banner de Fina Presta en el Home (`line-home-banner`, presta-home), literal del
 * prototipo. Única fuente: un `kind` nuevo no compila hasta tener su caso (`assertNever`).
 * `null` = ese estado no pinta banner en el Home (en revisión, cupo menor al mínimo, la pausa del
 * día y el recálculo abierto se ven solo dentro del módulo: la pausa diaria es transitoria y se
 * resuelve sola mañana, y el recálculo se responde en la pantalla de la línea, así que el Home no
 * avisa de ellos). El monto viaja como número: lo formatea el componente con `formatLineUsd`.
 *
 * `ready` dice siempre «¡Felicidades! Subiste al Nivel N.» (también en el Nivel 2) y `paused`
 * nombra siempre el cupo, como el diseño. Sin acceso invita a llegar al Nivel 2; nombra el cupo
 * solo si alcanza el mínimo pedible.
 */
export function lineHomeBannerCopy(p: LinePresentation): BannerCopy | null {
    switch (p.kind) {
        case 'ready':
            return {
                segments: [
                    textSegment(`¡Felicidades! Subiste al Nivel ${p.level}. Acabas de desbloquear `),
                    LOGO_SEGMENT,
                    textSegment(' con una línea de crédito de hasta '),
                    amountSegment(p.cupoUSD),
                    textSegment('.'),
                ],
                cta: 'Ver mi préstamo',
            };
        case 'paused':
            return {
                segments: [textSegment('Tu línea de '), LOGO_SEGMENT, textSegment(` está en pausa. ${LINE_PAUSED_ACTION}`)],
                cta: 'Ver mi préstamo',
            };
        case 'no_access': {
            const cupo = Number(p.cupoUSD) || 0;
            const lead = textSegment(`Tu préstamo está más cerca de lo que crees. Llega al Nivel ${LINE_BASE_LEVEL} y activa `);
            return {
                segments:
                    cupo >= LINE_MIN_REQUEST_USD
                        ? [lead, LOGO_SEGMENT, textSegment(' con una línea de crédito de hasta '), amountSegment(cupo), textSegment('.')]
                        : [lead, LOGO_SEGMENT, textSegment('.')],
                cta: 'Ver cómo llegar',
            };
        }
        case 'in_loan':
            return {
                segments:
                    p.amountUSD != null
                        ? [textSegment('Tienes un préstamo activo en '), LOGO_SEGMENT, textSegment(' de '), amountSegment(p.amountUSD), textSegment('. Págalo a tiempo para subir de nivel.')]
                        : [textSegment('Tienes un préstamo activo en '), LOGO_SEGMENT, textSegment('. Págalo a tiempo para subir de nivel.')],
                cta: 'Ver mis cuotas',
            };
        case 'off':
            return { segments: [textSegment('Tu línea de '), LOGO_SEGMENT, textSegment(' está apagada.')], cta: 'Ver mi préstamo' };
        case 'in_review':
        case 'below_floor':
        case 'paused_today':
        case 'recalc_pending':
            return null;
        default:
            return assertNever(p);
    }
}

/** Tile del banner del Home (el mismo dato que el tile de rayo del héroe de ofertas). */
export const LINE_HOME_BANNER_TILE = { label: 'Recibe tu dinero en menos de', value: '30 min' };

/**
 * Tinta del acento de la línea v2 para lo que se pinta desde TypeScript (anillo de progreso del
 * préstamo). Espejo de `$cr-cyan-ink` (`styles/_tokens.scss`): si cambia allá, cambia aquí.
 */
export const LINE_ACCENT_INK = '#0e7f99';

/** Pill de la card del nivel actual: siempre «Activo». El estado de la línea va en el pie. */
export const LINE_LEVEL_CURRENT_LABEL = 'Activo';

/** Pie de la card del nivel actual (`line-levels`): CTA, texto propio del estado o el motivo de bloqueo. */
export type LineLevelFooter = { type: 'cta' } | { type: 'text'; text: string } | { type: 'notice' };

/**
 * Pie de la card del nivel actual por `kind`. Única fuente: un `kind` nuevo no compila hasta tener
 * su caso. `notice` = el pie es el título de `resolveLineNotice` (sin acceso, cupo bajo, pausa del
 * día, recálculo abierto).
 */
export function lineLevelFooterCopy(p: LinePresentation): LineLevelFooter {
    switch (p.kind) {
        case 'ready':
            return { type: 'cta' };
        case 'in_loan':
            return { type: 'text', text: 'Tienes un préstamo en curso' };
        case 'paused':
            return { type: 'text', text: 'Línea en pausa' };
        case 'off':
            return { type: 'text', text: 'Línea apagada' };
        case 'in_review':
            return { type: 'text', text: 'Tu depósito está en camino' };
        case 'no_access':
        case 'below_floor':
        case 'paused_today':
        case 'recalc_pending':
            return { type: 'notice' };
        default:
            return assertNever(p);
    }
}

/** Pill y subtítulo de cada card de nivel. La pill de `current` es `LINE_LEVEL_CURRENT_LABEL`. */
export const LINE_LEVEL_STATE_COPY = {
    done: { label: 'Superado', subtitle: 'Nivel superado' },
    current: { subtitle: 'Tu nivel actual' },
    next: { label: 'Siguiente', subtitle: 'Próximo nivel' },
    locked: { label: 'Bloqueado', subtitle: 'Por desbloquear' },
};

/** Sección «Niveles y beneficios» (`line-levels`). */
export const LINE_LEVELS_COPY = {
    title: 'Niveles y beneficios',
    badge: 'Si pagas a tiempo',
    levelEyebrow: (level: number): string => `Nivel ${level}`,
    upTo: 'hasta',
    installments: (count: number, frecuencia: string): string => `${count} ${plural(count, 'cuota', 'cuotas')} ${frecuencia}`,
    howTo: 'Cómo llegas',
    activityRow: 'Registra tus movimientos semanales',
    /** Con la línea sin acceso (`not_eligible`): la entrada pide 60 días de movimientos (18/10). */
    activityRowLocked: 'Registra tus movimientos durante los primeros 60 días',
    subscriptionRow: 'Paga la suscripción de Fina a tiempo',
    loansRow: 'Paga tus préstamos a tiempo',
    cta: 'Solicitar préstamo',
    doneFooter: 'Desbloqueado',
};

/** «¿Cómo funciona?» (`line-how-it-works`). La cadencia sale de `line.terms`. */
export const LINE_HOW_IT_WORKS_COPY = {
    title: '¿Cómo funciona?',
    badge: '4 pasos',
    steps: (frecuenciaPlural: string): { title: string; body: string }[] => [
        { title: 'Elige tu monto', body: 'Escoge cuánto necesitas, hasta el tope de tu nivel. Verás al instante tus cuotas y el total.' },
        { title: 'Completa tu solicitud (30 seg)', body: 'Dinos dónde recibir el préstamo. Te lo enviamos en bolívares a la tasa BCV del día.' },
        { title: 'Recibe en 30 min', body: 'Nuestro equipo verifica y deposita el dinero en tu cuenta rápidamente.' },
        { title: 'Paga y sube de nivel', body: `Paga o abona tus cuotas ${frecuenciaPlural} a tiempo y desbloquea montos más altos.` },
    ],
    /** Con `currentStep` (solicitud aprobada): los pasos anteriores, el actual y los siguientes. */
    stepState: { done: 'Listo', current: 'Estás aquí', next: 'Después' },
};

/** Card de solicitud con la calculadora (`credit-line`). */
export const LINE_REQUEST_CARD_COPY = {
    level: (level: number): string => `Nivel ${level}`,
    stepAmount: '1. Elige tu monto',
    selectedAmount: 'Monto seleccionado',
    minAmount: (amount: string): string => `Mín. $${amount}`,
    maxAmount: (amount: string): string => `Máx. $${amount}`,
    stepTotal: '2. Total a pagar',
    installments: (count: number, frecuencia: string): string => `${count} ${plural(count, 'cuota', 'cuotas')} ${frecuencia}`,
    cuotaEyebrow: (n: number): string => `CUOTA ${n}`,
    cuotaDue: (date: string): string => `Vence el ${date}`,
    amountGroup: 'Monto del préstamo',
    cta: 'Solicitar préstamo',
};

/**
 * Monto personalizado de la calculadora (18/10): «Personalizado» con modal en escritorio, monto editable
 * en mobile. Los montos llegan formateados.
 */
export const LINE_CUSTOM_AMOUNT_COPY = {
    option: 'Personalizado',
    optionAria: (amount: string | null): string => (amount ? `Monto personalizado: $${amount}. Cambiar` : 'Elegir un monto personalizado'),
    hint: (min: string, max: string): string => `¿Otro monto? Usa «Personalizado»: entre $${min} y $${max}, de $${LINE_AMOUNT_STEP_USD} en $${LINE_AMOUNT_STEP_USD}.`,
    title: 'Elige tu monto',
    range: (min: string, max: string): string => `Entre $${min} y $${max}, de $${LINE_AMOUNT_STEP_USD} en $${LINE_AMOUNT_STEP_USD}.`,
    inputLabel: 'Monto del préstamo en dólares',
    less: `Restar $${LINE_AMOUNT_STEP_USD}`,
    more: `Sumar $${LINE_AMOUNT_STEP_USD}`,
    max: (amount: string): string => `Máx. $${amount}`,
    total: 'Total a pagar',
    installmentsOf: (installments: string): string => `${installments} de`,
    cancel: 'Cancelar',
    use: (amount: string): string => `Usar $${amount}`,
    useAny: 'Usar este monto',
    empty: (min: string, max: string): string => `Escribe un monto entre $${min} y $${max}.`,
    rounded: (from: string, to: string): string => `Ajustamos $${from} a $${to}: los montos van de $${LINE_AMOUNT_STEP_USD} en $${LINE_AMOUNT_STEP_USD}, hasta tu máximo.`,
    tapToEdit: 'Toca el monto para escribir otro',
    editAria: (amount: string): string => `Escribir otro monto. Ahora $${amount}`,
    done: 'Listo',
};

/**
 * Pausa del día en la card de solicitud (opción J del 18/10): el título es el de
 * `LINE_BLOCK_NOTICES.daily_limit`; a la derecha, lo que podrá pedir mañana con la línea de hoy. Sin
 * cifras del tope diario (DT-34). Los montos llegan formateados.
 */
export const LINE_DAILY_PAUSE_COPY = {
    body: 'Hemos tenido una alta demanda el día de hoy y el banco está presentando fallas. Estamos corrigiéndolo lo antes posible para que no vuelva a ocurrir💜',
    cta: 'Disponible mañana',
    eyebrow: 'Lo que podrás pedir mañana',
    upTo: 'hasta',
    level: 'Nivel',
    levelValue: (level: number, top: number | null): string => (top ? `${level} de ${top}` : `${level}`),
    installments: 'Cuotas',
    installmentsValue: (count: number, frecuencia: string): string => `${count} ${frecuencia}`.trim(),
    ifYouAsk: (amount: string): string => `Si pides $${amount}`,
    ifYouAskValue: (count: number, amount: string): string => `${count} ${plural(count, 'cuota', 'cuotas')} de $${amount}`,
    note: 'Según tu línea de hoy.',
};

/** Sin acceso a la línea (`not_eligible`): cabecera de la card de solicitud, literal del diseño. */
export const LINE_LOCKED_COPY = {
    /** «Nivel 2 de 5 · Línea inactiva»; sin la escalera, «Nivel 2 · Línea inactiva». */
    pill: (level: number, topLevel: number | null): string => `Nivel ${level}${topLevel ? ` de ${topLevel}` : ''} · Línea inactiva`,
    title: 'Desbloquea tu línea de crédito',
    body: LINE_LOCKED_BODY,
    available: (level: number): string => `Disponible desde el Nivel ${level}`,
};

/**
 * Página de solicitud (`/creditos/general/solicitud`, `line-request-page`): formulario solo
 * Transferencia y aside «Resumen». Los montos llegan ya formateados; las fechas, como etiqueta.
 */
/** Escalones de «¿Cuál sería el monto ideal…?»; se ofrecen los que superan el cupo actual. */
export const LINE_IDEAL_AMOUNT_TIERS: ReadonlyArray<number> = [500, 750, 1000, 1500, 2000, 3000, 4000, 5000, 7500, 10000, 15000, 20000];
/** Cuántos escalones se ofrecen antes de «Más de $X». */
export const LINE_IDEAL_AMOUNT_COUNT = 5;

export interface LineIdealAmountOptions {
    /** `value: null` = «Más de $X», que pide el monto. */
    options: ReadonlyArray<{ label: string; value: number | null }>;
    /** El monto escrito en «Más de $X» tiene que superar esto. */
    customMin: number;
}

/**
 * Opciones del monto ideal a futuro (18/10): solo montos **mayores al cupo actual** del comercio
 * (los 5 primeros escalones por encima) y «Más de $X» con el último. Con un cupo de $450 son las
 * del v1 ($500 a $2000 y «Más de $2000»). Sin escalones por encima, solo «Más de $<cupo>».
 */
export function lineIdealAmountOptions(cupoUSD: number | null | undefined): LineIdealAmountOptions {
    const cupo = Number(cupoUSD) > 0 ? Number(cupoUSD) : 0;
    const above = LINE_IDEAL_AMOUNT_TIERS.filter(v => v > cupo).slice(0, LINE_IDEAL_AMOUNT_COUNT);
    const customMin = above.length ? above[above.length - 1] : Math.floor(cupo);
    return {
        options: [...above.map(v => ({ label: `$${v}`, value: v })), { label: `Más de $${customMin}`, value: null }],
        customMin,
    };
}

/** Motivos del préstamo: los ids de `LOAN_PURPOSE_LABELS` de `api` (lo valida al recibirlos). */
export const LINE_LOAN_PURPOSE_OPTIONS: ReadonlyArray<{ value: 'mercancia' | 'remodelacion' | 'nomina' | 'otros'; label: string }> = [
    { value: 'mercancia', label: 'Compra de mercancía' },
    { value: 'remodelacion', label: 'Remodelación de local' },
    { value: 'nomina', label: 'Gastos operativos' },
    { value: 'otros', label: 'Otros' },
];

/** Espejo de `LOAN_PURPOSE_OTHER_MAX` de `api` (largo del motivo «Otros»). */
export const LINE_LOAN_PURPOSE_OTHER_MAX = 500;

export const LINE_REQUEST_FORM_COPY = {
    idealTitle: '¿Cuál sería el monto ideal para ti en el futuro?',
    idealCustomLabel: (min: number): string => `Indica tu monto ideal (mayor a $${min})`,
    /** Un ejemplo por encima del mínimo (con $2000 sigue siendo 3500, como el v1). */
    idealCustomPlaceholder: (min: number): string => `Ej: ${Math.round((min * 1.75) / 500) * 500}`,
    idealCustomError: (min: number): string => `El monto debe ser mayor a $${min}.`,
    purposeTitle: '¿Para qué vas a utilizar este préstamo?',
    purposeOtherPlaceholder: 'Especifica el motivo',
    back: 'Volver a Fina Presta',
    heading: 'Completa tu solicitud',
    subtitle: 'Dinos dónde quieres recibir tu dinero. Te lo enviamos en bolívares a la tasa BCV del día.',
    bank: 'Banco',
    cedulaRif: 'Cédula o RIF',
    /** Letra del documento (select) y número (campo): el body los manda juntos, `V12345678`. */
    cedulaRifTypes: ['V', 'E', 'J', 'P', 'G'] as ReadonlyArray<'V' | 'E' | 'J' | 'P' | 'G'>,
    cedulaRifTypeLabel: 'Tipo de documento',
    cedulaRifNumberLabel: 'Número de cédula o RIF',
    cedulaRifPlaceholder: '12345678',
    cedulaRifError: 'Ingresa de 6 a 9 números.',
    account: 'Número de cuenta (20 dígitos)',
    accountPlaceholder: '0102 0000 0000 0000 0000',
    accountError: 'El número de cuenta debe tener 20 dígitos.',
    /**
     * Checkbox de términos. `blockDay` sale de `LATE_GRACE_CALENDAR_DAYS + 1` (bloqueo al día
     * siguiente a la gracia): nunca un literal. Sin vencimiento conocido, sin «la primera el».
     */
    terms: (total: string, count: number, frecuencia: string, cuota: string, firstDue: string | null, blockDay: number): string =>
        `Acepto los términos: pagar $${total} en ${count} ${plural(count, 'cuota', 'cuotas')} ${frecuencia} de $${cuota}${
            firstDue ? `, la primera el ${firstDue}` : ''
        }. Entiendo que pagar con ${blockDay} o más días de atraso bloquea mi cuenta.`,
    viewContract: 'Ver contrato',
    openContract: 'Abrir contrato',
    submit: 'Confirmar solicitud',
    submitting: 'Enviando…',
    summary: 'Resumen',
    amount: 'Monto solicitado',
    installment: (n: number, due: string | null): string => (due ? `Cuota ${n} · ${due}` : `Cuota ${n}`),
    total: 'Total a pagar',
};

/**
 * Solicitud de recálculo (`line-recalc`, DT-12 + DT-42): monto obligatorio y mensaje. Los montos de texto
 * llegan formateados; el del placeholder, como número (el campo solo acepta dígitos).
 */
export const LINE_RECALC_COPY = {
    title: '¿Crees que tu cupo debería ser mayor?',
    openPill: 'En revisión',
    openBody: 'Recibimos tu solicitud de recálculo. El equipo de Fina la está revisando y te responderá pronto.',
    requestedAmount: (amount: string): string => `Monto solicitado: $${amount}`,
    answeredPill: 'Respondida',
    answeredFallback: 'El equipo de Fina revisó tu solicitud.',
    next: (date: string): string => `Podrás pedir otro recálculo a partir del ${date}.`,
    amountLabel: '¿Qué monto solicitas?',
    amountPlaceholder: (min: number): string => `Ej: ${min}`,
    /** Siempre bajo el campo; en rojo si ya se tocó y no cumple. */
    amountHint: (min: string): string => `Desde $${min}, de $${LINE_AMOUNT_STEP_USD} en $${LINE_AMOUNT_STEP_USD}: tiene que ser mayor a tu cupo actual.`,
    messageLabel: 'Cuéntanos por qué. El equipo de Fina lo revisará a mano.',
    messagePlaceholder: 'Por ejemplo: mis ventas crecieron este mes con la nueva sucursal.',
    submit: 'Pedir recálculo',
    sending: 'Enviando…',
};

/**
 * Crédito `requested` de la línea (`line-in-review`, opción D del 18/10: la v1 con el diseño v2): la
 * solicitud ya está aprobada y el depósito en camino. Los montos llegan formateados.
 */
export const LINE_IN_REVIEW_COPY = {
    title: 'Depósito en camino',
    body: 'Tu solicitud fue aprobada y tu depósito ya está en camino. En algunos casos el proceso toma un poco más de tiempo, así que te pedimos estar atento a tu cuenta.',
    waiting: 'Solicitud aprobada · depósito en camino',
    summaryTitle: 'Así quedará tu préstamo',
    summaryNote: 'Las fechas se fijan el día del depósito',
    receive: 'Recibes',
    total: 'Total a pagar',
    installments: (n: number, frecuenciaPlural: string): string => `${n} ${n === 1 ? 'cuota' : 'cuotas'}${frecuenciaPlural ? ` ${frecuenciaPlural}` : ''} de`,
    installmentLabel: (n: number): string => `Cuota ${n} · fecha estimada`,
};

/**
 * Depósito demorado (opción G del 18/10): pasados `LINE_DEPOSIT_DELAY_MINUTES` desde `requestedAt` con el
 * crédito todavía `requested`, la franja, el aviso, el botón y el paso 3 de «¿Cómo funciona?» pasan a ámbar.
 */
export const LINE_DEPOSIT_DELAY_COPY = {
    strip: { text: 'Tu depósito está', hi: 'en proceso', sub: 'Tu préstamo ya está aprobado', tone: 'warn' } as LineStripCopy,
    title: 'Tu depósito está tardando más de lo normal',
    body:
        'Tenemos alta demanda y el banco está procesando las transferencias más lento de lo habitual. ' +
        'Tu préstamo ya está aprobado y lo depositaremos en cuanto se normalice. No tienes que hacer nada.',
    waiting: 'Solicitud aprobada · depósito en proceso',
    step: { state: 'Tomando más tiempo', title: 'Recibe tu dinero', body: 'Hay alta demanda: el depósito puede tardar un poco más.' },
};

/** Modal de bienvenida (`line-welcome-modal`, `Modal-yqb1` del tablero del 16/10). */
export const LINE_WELCOME_COPY = {
    eyebrow: 'Exclusivo para ti',
    titleStrong: '¡Tienes',
    titleMiddle: ' un préstamo de hasta ',
    titleEnd: ' con Fina!',
    body: 'Pídelo cuando lo necesites, sin papeleo ni filas. Y mientras pagas a tiempo, tu cupo crece.',
    levelLabel: 'Empiezas en',
    /** «Nivel 2 de 5»; sin la escalera (`levels`), solo «Nivel 2». */
    levelValue: (level: number, topLevel: number | null): string => (topLevel ? `Nivel ${level} de ${topLevel}` : `Nivel ${level}`),
    speedLabel: 'Recibe tu dinero en',
    speedValue: 'Menos de 30 min',
    ladderTitle: 'Sube de nivel pagando a tiempo',
    ladderUpTo: (cap: string): string => `Hasta $${cap}`,
    ladderStep: (level: number, current: boolean): string => (current ? `Nivel ${level} · hoy` : `Nivel ${level}`),
    /** Monto de cada nivel de la escalera: «Hasta $750» (el tope llega formateado). */
    ladderCap: (cap: string): string => `Hasta ${cap}`,
    primary: 'Ver mi línea de crédito',
    secondary: 'Ahora no',
};

/** Modal de préstamo pagado o subida de nivel (`line-done-modal`). */
export const LINE_DONE_COPY = {
    eyebrow: '¡Préstamo completado!',
    levelUpTitle: (level: number): string => `¡Subiste a Nivel ${level}!`,
    levelUpBody: (cupo: string, count: number, frecuencia: string): string =>
        `Tu nuevo nivel ya está activo: hasta $${cupo} en ${count} ${plural(count, 'cuota', 'cuotas')} ${frecuencia}. Puedes pedirlo cuando quieras.`,
    levelUpCta: 'Pedir mi próximo préstamo',
    paidTitle: 'Pagaste tu préstamo',
    paidBody: (cupo: string): string => `Ya puedes pedir otro de hasta $${cupo}.`,
    ok: 'Entendido',
    maxLevel: 'Estás en el nivel máximo.',
    allMet: (level: number): string => `Ya cumples lo necesario para el Nivel ${level}.`,
    markPending: 'Tienes una marca activa: no subes hasta levantarla con cuotas puntuales seguidas.',
    remaining: (level: number, loans: number): string => `Para el Nivel ${level}: paga ${loans} ${plural(loans, 'préstamo', 'préstamos')} más a tiempo.`,
    markNote: (type: CreditLineMark['type'], needed: number): string =>
        `Tienes una marca ${type === 'grave' ? 'grave' : 'de atraso'} activa: paga ${needed} cuotas puntuales seguidas para levantarla y volver a subir.`,
    ladderLevel: (level: number): string => `Nivel ${level}`,
    /** Etiqueta del monto de cada nivel («Hasta» sobre «$750»: las fichas son estrechas). */
    ladderUpTo: 'Hasta',
    ladderCuotas: (count: number): string => `${count} ${plural(count, 'cuota', 'cuotas')}`,
};

/** Selector de banco con búsqueda (`fina-bank-select`): pago, abono, bloqueo y solicitud de Fina Presta. */
export const BANK_SELECT_COPY = {
    placeholder: 'Selecciona tu banco',
    searchPlaceholder: 'Código o nombre',
    empty: 'No encontramos ese banco',
    toggle: 'Ver bancos',
};

/**
 * Pago o abono sin validar en la tarjeta de la cuota (Fina Presta): reemplaza a «Pagos por validar».
 * Mientras exista, las demás cuotas no se pueden pagar.
 */
export const LINE_VALIDATION_HOLD_COPY = {
    correct: 'Corregir',
};

/**
 * Cuota abierta con un abono del comercio sin validar (Fina Presta, opción F del 18/10): en grande la
 * cuota total, debajo lo abonado «(en verificación)» y lo que resta. Sin referencia, fecha ni motivo del
 * banco: «en verificación» ya dice que no se validó solo. Los montos llegan formateados.
 */
export const LINE_ABONO_VERIFY_COPY = {
    totalLabel: 'Cuota total',
    paid: (paid: string): string => `Abonado: $${paid}`,
    verifying: '(en verificación)',
    rest: (rest: string): string => `Restan: $${rest}`,
    correct: 'Corregir abono',
};

// ─── Verificación del teléfono antes de pedir (ADM-813, diseño aprobado el 25/10) ──────────────

/**
 * Texto con una parte resaltada (`<strong>` en la plantilla): `before + value + after` es el literal del
 * diseño. Así el copy completo vive aquí y la plantilla solo decide qué va en negrita.
 */
export interface CopyParts {
    before: string;
    value: string;
    after: string;
}

/**
 * `fina-phone-verification` y la franja de `line-request-page`. Lo dibujado en los artboards y, al final,
 * los estados que no se dibujaron (mismo tono). El copy de Mi cuenta vive en el módulo `account`.
 */
export const PHONE_VERIFICATION_COPY = {
    stepLabel: 'Paso 1 de 2',
    formStepLabel: 'Paso 2 de 2',
    verifyTitle: 'Verifica tu teléfono',
    verifySubtitle: 'Antes de pedir tu crédito, confirma que este número es tuyo. Ahí te avisamos de tus cuotas y de tu depósito.',
    currentLabel: 'Tu número',
    notMine: 'No es mi número',
    channelLegend: '¿Por dónde quieres recibir el código?',
    sms: 'SMS',
    smsHint: 'Mensaje de texto',
    whatsapp: 'WhatsApp',
    whatsappHint: 'Mensaje de Fina',
    send: 'Enviar código',
    sending: 'Enviando…',
    sendHint: 'Es un código de 6 dígitos y vence en 10 minutos.',
    codeTitle: 'Escribe el código',
    codeSubtitle: (channel: string, display: string): CopyParts => ({ before: `Te lo enviamos por ${channel} al `, value: display, after: '.' }),
    codeLegend: 'Código de 6 dígitos',
    digitLabel: (n: number): string => `Dígito ${n}`,
    notReceived: '¿No te llegó?',
    resendIn: (time: string): string => `Reenviar en ${time}`,
    resendBy: (channel: string): string => `Reenviar por ${channel}`,
    sendByOther: (channel: string): string => `Enviar por ${channel}`,
    verify: 'Verificar',
    verifying: 'Verificando…',
    codeExpiryHint: 'El código vence en 10 minutos.',
    codeInvalid: (n: number): string => `El código no es correcto. Te quedan ${n} ${plural(n, 'intento', 'intentos')}.`,
    codeAttemptsHint: 'Al quinto intento fallido tendrás que pedir un código nuevo.',
    changeTitle: 'Cambia tu número',
    changeSubtitle: 'Escribe el celular que usas hoy. Para proteger tu cuenta, te pedimos tu contraseña.',
    phoneLabel: 'Número de celular',
    phonePlaceholder: '0412 123 4567',
    phoneHint: 'Celular venezolano: 0412, 0414, 0416, 0422, 0424 o 0426.',
    phoneIncomplete: 'Escribe los 11 dígitos de tu celular, por ejemplo 0412 123 4567.',
    passwordLabel: 'Contraseña',
    passwordPlaceholder: 'Tu contraseña de Fina',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    passwordInvalid: 'La contraseña no es correcta. Revísala e intenta de nuevo.',
    changeNote: (display: string): CopyParts => ({ before: 'Tu número actual (', value: display, after: ') no cambia hasta que confirmes el código que te enviaremos al nuevo.' }),
    cancel: 'Cancelar',
    invalidTitle: 'Escribe tu celular',
    invalidSubtitle: 'Para proteger tu cuenta, te pedimos tu contraseña.',
    invalidNotice: (raw: string): CopyParts => ({ before: 'El número que tenemos registrado (', value: raw, after: ') no es un celular. Escribe el celular que usas hoy para recibir el código.' }),
    verifiedStrip: (display: string, channel: string): CopyParts => ({ before: '', value: 'Teléfono verificado.', after: ` ${display}, por ${channel}.` }),
    // Estados que no se dibujaron (propuestos por el plan, mismo tono).
    codeExpired: 'El código venció. Pide uno nuevo.',
    codeMaxAttempts: 'Agotaste los intentos. Pide un código nuevo.',
    // DT-1 de ADM-813: la cuota de `api` es de 5 envíos cada 30 minutos (no por día). Sin «mañana» ni `retryAt`.
    sendLimit: 'Llegaste al máximo de códigos. Intenta de nuevo en unos minutos.',
    sendFailed: 'No pudimos enviar el código. Intenta de nuevo o usa el otro canal.',
    reauth: 'Por seguridad, vuelve a escribir tu contraseña.',
    passwordLocked: 'Hiciste demasiados intentos. Espera unos minutos e intenta de nuevo.',
    rateLimited: 'Vas muy rápido. Espera un momento e intenta de nuevo.',
    sameNumber: 'Ese ya es tu número actual.',
    phoneChanged: 'Tu número cambió. Vuelve a verificarlo.',
    statusFailed: 'No pudimos cargar tu teléfono.',
    retry: 'Reintentar',
    // Códigos sin clave propia en el diseño.
    phoneInvalid: 'Ese número no es un celular venezolano.',
    codeNotFound: 'Ese código ya no sirve. Pide uno nuevo.',
    supportForbidden: 'Desde una sesión de soporte no se puede verificar ni cambiar el teléfono.',
    genericError: 'Algo salió mal. Intenta de nuevo.',
};

/** Forma del copy de `fina-phone-verification`; otro flujo puede pisar claves con `copyOverrides`. */
export type PhoneVerificationCopy = typeof PHONE_VERIFICATION_COPY;

/** Claves de `PHONE_VERIFICATION_COPY` que son un texto fijo (las que puede pintar un error). */
export type PhoneVerificationMessage =
    | 'genericError'
    | 'phoneInvalid'
    | 'sameNumber'
    | 'passwordInvalid'
    | 'reauth'
    | 'codeNotFound'
    | 'codeExpired'
    | 'codeMaxAttempts'
    | 'sendLimit'
    | 'phoneChanged'
    | 'sendFailed'
    | 'supportForbidden'
    | 'rateLimited'
    | 'passwordLocked';

/** Pedido que originó el error: decide si se relee el estado (`reread`). */
export type PhoneVerificationRequest = 'send' | 'change' | 'resend' | 'confirm';

export interface PhoneErrorHandling {
    /** Texto que se pinta; `null` = sin mensaje (pasa directo, como `PHONE_ALREADY_VERIFIED`). */
    copy: PhoneVerificationMessage | null;
    /**
     * Dónde: campo del número o de la contraseña, bajo las casillas, aviso del paso o toast. Si además se
     * relee el estado, los destinos de campo se descartan (el paso se vuelve a pintar); `notice` y `toast`
     * se conservan.
     */
    target: 'phone' | 'password' | 'code' | 'notice' | 'toast';
    /** Releer el estado: siempre (`true`) o solo si el error vino de esos pedidos. */
    reread?: true | ReadonlyArray<PhoneVerificationRequest>;
    /** El código actual ya no sirve: «Verificar» se apaga hasta reenviar. */
    newCode?: true;
    /** Paso al que vuelve (`PHONE_CHANGE_REAUTH_REQUIRED` pide la contraseña otra vez). */
    step?: 'change';
    /** El servidor inutilizó el código pendiente si el error vino de esos pedidos: se descarta. */
    discardCode?: ReadonlyArray<PhoneVerificationRequest>;
}

/**
 * Qué hace la pantalla con cada código de `/users/me/phone-verification` (ADM-813). El copy sale de
 * `PHONE_VERIFICATION_COPY`, nunca del `message` del servidor. Un código que no está aquí, un error sin
 * `code` o de red caen a `genericError`. Excepciones con lógica propia en el componente:
 * `PHONE_CODE_INVALID` (`attemptsRemaining`; con 0 o menos, igual que `PHONE_CODE_MAX_ATTEMPTS`) y
 * `PHONE_CODE_COOLDOWN` (reajusta el contador con `resendAvailableAt` / `waitSeconds`).
 */
export const PHONE_ERROR_HANDLING: Partial<Record<PhoneErrorCode, PhoneErrorHandling>> = {
    PHONE_VERIFICATION_INVALID_BODY: { copy: 'genericError', target: 'notice' },
    // En `change`, el número escrito; en `send`/`resend`, el guardado dejó de ser válido y se entra a `change`.
    PHONE_INVALID: { copy: 'phoneInvalid', target: 'phone', reread: ['send', 'resend'] },
    PHONE_ALREADY_VERIFIED: { copy: null, target: 'notice', reread: true },
    PHONE_SAME_NUMBER: { copy: 'sameNumber', target: 'phone' },
    PHONE_CHANGE_INVALID_PASSWORD: { copy: 'passwordInvalid', target: 'password' },
    PHONE_CHANGE_REAUTH_REQUIRED: { copy: 'reauth', target: 'password', step: 'change' },
    PHONE_CODE_NOT_FOUND: { copy: 'codeNotFound', target: 'notice', reread: true },
    PHONE_CODE_EXPIRED: { copy: 'codeExpired', target: 'code', newCode: true },
    PHONE_CODE_MAX_ATTEMPTS: { copy: 'codeMaxAttempts', target: 'code', newCode: true },
    PHONE_CODE_SEND_LIMIT: { copy: 'sendLimit', target: 'toast' },
    PHONE_CHANGED: { copy: 'phoneChanged', target: 'notice', reread: true },
    // Cualquier envío fallido (`send`, `change` o `resend`) deja `codeHash: null` en `api`: el código pendiente
    // ya no sirve. Tras `send`/`change` se descarta (sin releer, para no perder lo escrito en `change`); tras
    // `resend` se relee el estado.
    PHONE_CODE_SEND_FAILED: { copy: 'sendFailed', target: 'toast', reread: ['resend'], discardCode: ['send', 'change'] },
    PHONE_VERIFICATION_SUPPORT_FORBIDDEN: { copy: 'supportForbidden', target: 'notice' },
    PHONE_VERIFICATION_RATE_LIMITED: { copy: 'rateLimited', target: 'toast' },
    PHONE_CHANGE_PASSWORD_LOCKED: { copy: 'passwordLocked', target: 'password' },
};
